import {
  schAlert,
  schAloftDate,
  schAloftPrice,
  schLocationType,
  schPropertyInterest,
  schSixTierQuality,
  schValueType,
  schZoningCompliance,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { ESCALATION_TYPE_MESSAGES, STOP_AND_CALL_VALUES } from './consts.ts';

/**
 *  Validation schema for Appraisify validation
 *  This schema is used purely for validation purposes, not for UI forms
 */
export const schAppraisifyValidation = z.object({
  // Alert validation
  alerts: z.object({
    alerts: z.array(schAlert).refine((alerts) => alerts.every((a) => a.status === 'dismissed'), {
      message: 'There are active alerts that need to be addressed',
    }),
  }),

  // Order Details validation
  orderDetails: z
    .object({
      zoning: z.string().min(1, 'Required'),
      zoningCompliance: schZoningCompliance,
      parcelNumber: z.string().optional(),
      newConstruction: z.boolean().default(false),
      legalDescription: z.string().min(1, 'Required'),
      locationType: schLocationType,
      hoa: z.string().min(1, 'Required'),
      effectiveDate: schAloftDate,
      intendedUsers: z.string().min(1, 'Required'),
      intendedUse: z.string().min(1, 'Required'),
      propertyInterest: schPropertyInterest,
      propertyInterestOther: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if ((!data.parcelNumber || data.parcelNumber.trim().length === 0) && !data.newConstruction) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['parcelNumber'],
        });
      }

      if (data.propertyInterest === 'Other') {
        if (!data.propertyInterestOther || data.propertyInterestOther.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['propertyInterestOther'],
          });
        }
      }
    }),

  // Analysis validation
  analysis: z.object({
    comps: z.array(z.string()).min(1, 'At least one comparable is required'),
    opinionOfSiteValue: schAloftPrice.refine(
      (price) => price.toDollarLike() > 0,
      'Opinion of site value must be greater than 0',
    ),
  }),

  // Market validation
  market: z.object({}),

  // Commentary validation
  commentary: z.object({
    estimatedDemand: z.string().min(1, 'Required'),
    estimatedExposureTime: z.string().min(1, 'Required'),
    marketConditionCommentary: z.string().min(1, 'Required'),
    priorSalesCommentary: z.string().min(1, 'Required'),
  }),

  // Reconciliation validation
  reconciliation: z
    .object({
      totalCostToCure: schAloftPrice,
      totalCostToCureCommentary: z.string().min(1, 'Required'),
      opinionOfMarketValue: schAloftPrice,
      opinionOfAfterRepairValue: schAloftPrice,
      totalRenovationBudget: schAloftPrice,
      includeTotalRenovationBudget: z.boolean(),
      includeOpinionOfAfterRepairValue: z.boolean(),
      includeTotalCostToCure: z.boolean(),
      hasArvOption: z.boolean(),
      reconciliationCommentary: z.string().min(1, 'Required'),
      highestAndBestUseCommentary: z.string().min(1, 'Required'),
      revisionCommentary: z.string().min(1, 'Required'),
      alternativeComparablesCommentary: z.array(
        z.string().min(1, 'Each alternative comparable must be addressed with a commentary'),
      ),
      feasibilityCommentary: z.string().min(1, 'Required'),
      aivDiscrepancyCommentary: z.string().optional(),
      arvDiscrepancyCommentary: z.string().optional(),
      hasAivDiscrepancy: z.boolean(),
      hasArvDiscrepancy: z.boolean(),
      isMockOrder: z.boolean(),
      valueTypes: z.array(schValueType).optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.isMockOrder && (!data.valueTypes || data.valueTypes.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a value type option',
          path: ['valueTypes'],
        });
      }

      if (data.hasAivDiscrepancy && !data.aivDiscrepancyCommentary?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['aivDiscrepancyCommentary'],
        });
      }

      if (data.hasArvDiscrepancy && !data.arvDiscrepancyCommentary?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['arvDiscrepancyCommentary'],
        });
      }
    }),
});

export type AppraisifyValidationData = z.infer<typeof schAppraisifyValidation>;

export const schAppraisifyStopAndCall = z.object({
  orderDetails: z
    .object({
      zoningCompliance: schZoningCompliance,
      propertyInterest: schPropertyInterest,
    })
    .partial()
    .superRefine((data, ctx) => {
      if (
        data.propertyInterest &&
        STOP_AND_CALL_VALUES.propertyInterest.includes(data.propertyInterest)
      ) {
        let message = '';
        if (data.propertyInterest === 'Leased Fee') {
          message = ESCALATION_TYPE_MESSAGES.leased_fee_property_interest;
        } else if (data.propertyInterest === 'Leasehold') {
          message = ESCALATION_TYPE_MESSAGES.leasehold_property_interest;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['propertyInterest'],
        });
      }
      if (
        data.zoningCompliance &&
        STOP_AND_CALL_VALUES.zoningCompliance.includes(data.zoningCompliance)
      ) {
        let message = '';
        if (data.zoningCompliance === 'Illegal') {
          message = ESCALATION_TYPE_MESSAGES.zoning_compliance_illegal;
        } else if (data.zoningCompliance === 'Legal Nonconforming') {
          message = ESCALATION_TYPE_MESSAGES.zoning_compliance_non_conforming;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ['zoningCompliance'],
        });
      }
    }),
  features: z.object({
    subject: z
      .object({
        uadCondition: schSixTierQuality,
      })
      .partial()
      .superRefine((data, ctx) => {
        if (data.uadCondition && STOP_AND_CALL_VALUES.uadCondition.includes(data.uadCondition)) {
          let message = '';

          if (data.uadCondition === 5) {
            message = ESCALATION_TYPE_MESSAGES.c5_condition;
          } else if (data.uadCondition === 6) {
            message = ESCALATION_TYPE_MESSAGES.c6_condition;
          }

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['uadCondition'],
          });
        }
      }),
  }),
  reconciliation: z
    .object({
      valueTypes: z.array(schValueType),
      allowSubjectToValue: z.boolean(),
    })
    .partial()
    .superRefine((data, ctx) => {
      if (
        data.valueTypes &&
        !data.allowSubjectToValue &&
        data.valueTypes.some((type) => STOP_AND_CALL_VALUES.valueTypes.includes(type))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ESCALATION_TYPE_MESSAGES.value_subject_to,
          path: ['valueTypes'],
        });
      }
    }),
});

export type AppraisifyStopAndCallData = z.infer<typeof schAppraisifyStopAndCall>;
